import React from 'react';
import clsx from 'clsx';
import useCenterlineAiStyles from './styles';
import { isLive, Turn } from './core/chat-reducer';
import { usageParts, usageText } from './core/usage';
import { AgentUsage } from './core/types';
import ToolIcon from './ToolIcon';
import Trace from './Trace';
import Answer from './Answer';
import FeedbackBar from './FeedbackBar';
import centerlineLogo from '../../images/centerline-logo-rounded.png';
import { UserAvatar } from './Glyphs';
import { splitReviewMessage } from './review/review-prompts';

/** Message rows (chat-ui-spec §1.6, §3.4, §5, §7). */

export function UserMessage({ question }: { question: string }) {
  const classes = useCenterlineAiStyles();
  // A document review's stored message is its long instructions; show the short label they open
  // with, and keep the instructions behind a disclosure.
  const review = splitReviewMessage(question);
  return (
    <div className={clsx(classes.row, classes.rowUser)}>
      <UserAvatar />
      {/* Plain text, never markdown. */}
      <div className={classes.userBubble}>
        {review ? review.label : question}
        {review && review.instructions ? (
          <details className={classes.instructions}>
            <summary>Instructions sent to CENTERLINE AI</summary>
            <pre>{review.instructions}</pre>
          </details>
        ) : null}
      </div>
    </div>
  );
}

export function ToolTag({ tool }: { tool: string }) {
  const classes = useCenterlineAiStyles();
  return (
    <div className={classes.msgTags}>
      <span className={clsx(classes.tagPill, classes.tagMono)} title={`Asked to use ${tool}`}>
        <ToolIcon tool={tool} size={12} />
        {tool}
      </span>
    </div>
  );
}

export function ContextTag({ used }: { used: { types: string[]; files: string[] } }) {
  const classes = useCenterlineAiStyles();
  return (
    <div className={classes.msgTags} aria-label="Documents this question was limited to">
      {used.types.map((t) => (
        <span key={`t:${t}`} className={classes.tagPill} title={`document type: ${t}`}>
          @{t}
        </span>
      ))}
      {used.files.map((name, k) => (
        // eslint-disable-next-line react/no-array-index-key
        <span key={`f:${k}`} className={classes.tagPill} title={name}>
          {name}
        </span>
      ))}
    </div>
  );
}

/** A restored turn that failed or was stopped (§3.4). */
export function ErrorMessage({ text }: { text: string }) {
  const classes = useCenterlineAiStyles();
  return (
    <div className={classes.row}>
      <div className={clsx(classes.avatar, classes.avatarError)} aria-hidden>
        !
      </div>
      <div className={classes.errorBubble} role="alert">
        {text}
      </div>
    </div>
  );
}

export function UsageStrip({ usage }: { usage: AgentUsage }) {
  const classes = useCenterlineAiStyles();
  return (
    <div className={classes.usage} aria-label={`Usage: ${usageText(usage)}`}>
      {usageParts(usage).map((p) => (
        <span key={p.label}>
          {p.label}
          {p.tight ? '' : ' '}
          <b>{p.value}</b>
        </span>
      ))}
    </div>
  );
}

export function BotMessage({ chatId, turn }: { chatId: number; turn: Turn }) {
  const classes = useCenterlineAiStyles();
  const { bot } = turn;
  return (
    <div className={classes.row}>
      <img className={classes.avatarLogo} src={centerlineLogo} alt="" width={32} height={32} />
      <div className={classes.botBubble}>
        {bot.traceVisible ? <Trace bot={bot} /> : null}
        {/* Announced once the answer settles, not token by token. */}
        <div aria-live={bot.restored ? undefined : 'polite'} aria-busy={isLive(bot)}>
          <Answer bot={bot} projectId={turn.projectId} />
        </div>
        {bot.usage ? <UsageStrip usage={bot.usage} /> : null}
        {bot.showFeedback && bot.turnId ? (
          <FeedbackBar chatId={chatId} turnKey={turn.key} feedback={bot.feedback} />
        ) : null}
      </div>
    </div>
  );
}

export function TurnView({ chatId, turn }: { chatId: number; turn: Turn }) {
  return (
    <>
      <UserMessage question={turn.question} />
      {turn.toolUsed ? <ToolTag tool={turn.toolUsed} /> : null}
      {turn.contextUsed ? <ContextTag used={turn.contextUsed} /> : null}
      {turn.bot.errorRow ? (
        <ErrorMessage text={turn.bot.message ?? ''} />
      ) : (
        <BotMessage chatId={chatId} turn={turn} />
      )}
    </>
  );
}
